/**
 * The sending-capacity chip — §5.3 made visible, §15.1.
 *
 * "12 of 34 sends used today" in the toolbar, expanding to the per-mailbox
 * truth: used against cap, warm-up day, throttle with its numbers. §15's rule —
 * throttle state is always visible on the mailbox, with the triggering
 * numbers — finally has somewhere to live that a rep actually looks.
 *
 * The numbers come from /capacity, which reuses the gates' own arithmetic
 * (`dailyCaps`), so this chip can never disagree with gate 4 about how much
 * room is left.
 */

import { absolute } from './api';
import { tip, tipAt } from './tips';
import { usePopover } from './usePopover';
import { useQuery } from './useQuery';

export interface MailboxCapacity {
  id: number;
  email: string;
  status: string;
  usedToday: number;
  pendingSends: number;
  /** Of `usedToday`, campaign sends — which leave from this mailbox without appearing in its queue (P14). */
  campaignSendsToday: number;
  cap: number;
  /** Cap minus used, floored at zero — one number, computed server-side. */
  remaining: number;
  limiting: string;
  throttlePct: number;
  throttleUntil: string | null;
  warmupDay: number | null;
  /** Trailing seven days — one bounce is noise, four is a pattern (§5.3). */
  bouncedWeek: number;
  unsubscribesWeek: number;
  lastBounceDay: string | null;
}

export interface Capacity {
  mailboxes: MailboxCapacity[];
  totals: { used: number; cap: number };
}

/**
 * One poll of `/capacity`, shared.
 *
 * The chip needs it and so does the warning beside Approve (R11). Two
 * components each polling the same URL every sixty seconds would double a
 * request §4 is trying to make cheaper, so the fetch lives here and both
 * callers read the same object — which also means they can never disagree
 * about how much room is left.
 */
export function useCapacity(): Capacity | null {
  /*
   * Through `useQuery`, which owns both halves this used to hand-roll: the
   * sixty-second poll and the skip while the tab is hidden. It also means the
   * chip is drawn from the last reading the moment the SDR area mounts instead
   * of appearing a round trip later — a control that pops into a toolbar after
   * the toolbar is already on screen is the small version of the same jump.
   *
   * A failed read stays silent, as it did: the caller's contract is
   * `Capacity | null`, and `CapacityChip` renders nothing for null. A banner
   * about a chip nobody asked for would be worse than the chip being absent.
   */
  const { data } = useQuery<Capacity>('/capacity', { refreshMs: 60_000 });
  return data ?? null;
}

export function CapacityChip({ data }: { data: Capacity | null }) {
  // Click-away and Escape dismissal: the panel is wide and sits over the queue,
  // and pressing the chip again was the only way to get rid of it.
  const { ref, open, toggle } = usePopover<HTMLDivElement>();

  // No mailboxes, no chip. "0 of 0" would read as a broken environment when it
  // just means nothing is connected yet — Settings says that properly.
  if (!data || data.mailboxes.length === 0) return null;

  const throttled = data.mailboxes.some((m) => m.throttlePct < 100);

  return (
    <div className="capacity" ref={ref}>
      <button
        type="button"
        className="capacity-chip"
        {...tipAt(
          'How much of today’s sending allowance is used, across your mailboxes — the same arithmetic the send gates apply. The day resets at midnight UTC. Opening this changes nothing.',
          'end',
        )}
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <span className={throttled ? 'capacity-dot warn' : 'capacity-dot'} />
        {data.totals.used} of {data.totals.cap} sends used today
      </button>

      {open && (
        <div className="capacity-panel">
          <p className="band-label">Sending mailboxes</p>
          {data.mailboxes.map((m) => (
            <div key={m.id} className="capacity-row">
              <span className={m.throttlePct < 100 ? 'capacity-dot warn' : 'capacity-dot'} />
              <span className="capacity-addr">{m.email}</span>
              <span
                className="capacity-used"
                {...tip('Sent today against the mailbox’s effective daily cap. Approved-but-unsent work is not counted until it goes.')}
              >
                {m.usedToday} / {m.cap}
              </span>
              <span className={m.throttlePct < 100 ? 'capacity-note warn' : 'capacity-note'}>
                {m.throttlePct < 100
                  ? `Throttled to ${m.throttlePct}%${m.throttleUntil ? ` until ${absolute(m.throttleUntil)}` : ''}`
                  : m.warmupDay !== null
                    ? `Warm-up day ${m.warmupDay} · ${m.limiting} cap binds`
                    : `${m.limiting} cap binds`}
              </span>
              {/*
                Deliverability under the volume — §5.3's other two observables.
                A mailbox with room left in its cap can still be the one heading
                for a throttle, and "12 of 34" never showed that. Only rendered
                when there is something to say, so a healthy mailbox stays one
                quiet line.
              */}
              {(m.bouncedWeek > 0 || m.unsubscribesWeek > 0 || m.pendingSends > 0 || m.campaignSendsToday > 0) && (
                <span className="capacity-health fine">
                  {[
                    m.pendingSends > 0 ? `${m.pendingSends} waiting to send` : null,
                    m.campaignSendsToday > 0 ? `${m.campaignSendsToday} campaign sends today` : null,
                    m.bouncedWeek > 0
                      ? `${m.bouncedWeek} bounced in 7d${m.lastBounceDay ? ` (last ${m.lastBounceDay})` : ''}`
                      : null,
                    m.unsubscribesWeek > 0 ? `${m.unsubscribesWeek} unsubscribed in 7d` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              )}
            </div>
          ))}
          <p className="fine">
            Caps ramp with warm-up and shrink under throttle (§5.3). {'“'}Binds{'”'} names
            which limit is the low one right now — mailbox, domain, or the environment budget.
            Bounces and unsubscribes are what trip a throttle, so they are shown before it does.
          </p>
        </div>
      )}
    </div>
  );
}
